import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Input } from '@playcastdotio/Shadcn';
import { ChangeEvent, useRef, useState, useEffect } from 'react';
import { faCircleStop, faEye, faEyeSlash, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { isElectron } from '../../api';
import { BeaconDisplayPicker } from './components/BeaconDisplayPicker';
import { BeaconShareMenu } from './components/BeaconShareMenu';
import { BeaconInputOptions } from './components/BeaconInputOptions';
import React, { memo } from 'react';
import {
  beaconCoordinator,
  displaySourceManager,
  audioManager,
  displayOutputManager,
  sessionCoordinator,
  appCoordinator,
} from '../../coordinators/init';
import { serviceContainer } from '../../services/ServiceContainer';
import { useDisplaySource } from '../../coordinators/media/hooks/useDisplaySource';
import { useStreamLifecycle } from '../../coordinators/session/hooks/useStreamLifecycle';
import { useAudioManager } from '../../coordinators/media/hooks/useAudioManager';
import { useDisplayOutput } from '../../coordinators/media/hooks/useDisplayOutput';
import { BeaconVolumeManager } from './components/BeaconVolumeManager';
import { BeaconDisplay } from './components/BeaconDisplay';
import type { DisplaySourceManager } from '../../coordinators/media/managers/DisplaySourceManager';
import type { AudioManager as AudioManagerType } from '../../coordinators/media/managers/AudioManager';
import type { DisplayOutputManager } from '../../coordinators/media/managers/DisplayOutputManager';

function BeaconVolumeSection({ manager }: { manager: AudioManagerType }) {
  const audioState = useAudioManager(manager);
  return (
    <BeaconVolumeManager
      desktopVolume={audioState.desktopVolume}
      micVolume={audioState.micVolume}
      isDesktopUnmuted={audioState.isDesktopUnmuted}
      isMicUnmuted={audioState.isMicUnmuted}
      desktopDevices={audioState.desktopDevices}
      micDevices={audioState.micDevices}
      selectedDesktopDevice={audioState.selectedDesktopDevice}
      selectedMicDevice={audioState.selectedMicDevice}
      onDesktopVolumeChange={audioState.onDesktopVolumeChange}
      onMicVolumeChange={audioState.onMicVolumeChange}
      onDesktopMuteToggle={audioState.onDesktopMuteToggle}
      onMicMuteToggle={audioState.onMicMuteToggle}
      onDesktopDeviceChange={audioState.onDesktopDeviceChange}
      onMicDeviceChange={audioState.onMicDeviceChange}
    />
  );
}

const LiveActionButton = memo(
  ({
    isLive,
    buttonText,
    handleStopStream,
    handleStartLoginStream,
  }: {
    isLive: boolean;
    buttonText: string;
    handleStopStream: () => void;
    handleStartLoginStream: () => void;
  }) => {
    const navigate = useNavigate();

    return isLive ? (
      <div className='flex w-full flex-col space-y-2 md:space-y-0'>
        <Button className='strong-shadows m-0 flex w-full p-0 text-white md:hidden' onClick={() => navigate('/playcast')}>
          Invite Players!
        </Button>
        <Button
          className='strong-shadows m-0 w-full gap-x-2 bg-orange-600 p-0 text-white hover:bg-orange-900 focus:bg-orange-900 active:bg-orange-900'
          onClick={handleStopStream}
        >
          <FontAwesomeIcon icon={faCircleStop} />
          Stop Playcasting
        </Button>
      </div>
    ) : (
      <Button
        className='embossed-primary strong-shadows m-0 w-full bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 font-extrabold opacity-100'
        onClick={handleStartLoginStream}
      >
        {buttonText}
      </Button>
    );
  },
);

function BeaconDisplayPickerWithManager({ manager }: { manager: DisplaySourceManager }) {
  const displaySource = useDisplaySource(manager);
  return (
    <BeaconDisplayPicker
      sources={displaySource.sources}
      selectedLabel={displaySource.selectedLabel}
      onSelectDesktop={displaySource.onSelectDesktop}
      onSelectSurfMode={displaySource.onSelectSurfMode}
      onSelectDesktopCapture={displaySource.onSelectDesktopCapture}
      onSelectCamera={displaySource.onSelectCamera}
      onSelectWindowCapture={displaySource.onSelectWindowCapture}
      onRefreshApps={displaySource.onRefreshApps}
    />
  );
}

function BeaconPreviewSection({
  showPreview,
  onTogglePreview,
}: {
  showPreview: boolean;
  onTogglePreview: () => void;
}) {
  return (
    <div className='relative'>
      {displayOutputManager && showPreview ? (
        <PreviewInner manager={displayOutputManager} />
      ) : (
        <div className='theme-midnight:bg-slate-800 bg-darker border-primary flex min-h-[80px] items-center justify-center rounded-lg border-2'>
          <span className='text-sm text-white/40'>Preview hidden</span>
        </div>
      )}
      <button
        type='button'
        onClick={onTogglePreview}
        className='absolute right-2 top-2 z-10 flex items-center gap-x-1.5 rounded bg-black/50 px-2 py-1 text-xs text-white/70 hover:bg-black/70 hover:text-white'
      >
        <FontAwesomeIcon icon={showPreview ? faEyeSlash : faEye} className='h-3 w-3' />
        {showPreview ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function PreviewInner({ manager }: { manager: DisplayOutputManager }) {
  const displayOutput = useDisplayOutput(manager);
  return (
    <BeaconDisplay
      displays={displayOutput.displays}
      selectedDisplayId={displayOutput.selectedDisplayId}
      isLive={displayOutput.isLive}
      elapsedTime={displayOutput.elapsedTime}
      isDisplaySelected={displayOutput.displays.length > 0}
      onDisplayClick={displayOutput.onDisplayClick}
      height='lg'
    />
  );
}

export default function BeaconStudio() {
  const [streamTitle, setStreamTitle] = useState<string>('');
  const streamTitleInputRef = useRef<HTMLInputElement>(null);
  const [streamUrl, setStreamUrl] = useState<string>('');
  const [showPreview, setShowPreview] = useState(true);
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    if (!beaconCoordinator) return;
    const store = beaconCoordinator.getBeaconStore();
    const sync = (s: typeof store.currentState) => setStreamUrl(s.shareLink ?? '');
    sync(store.currentState);
    const listenerId = store.addListener(sync);
    return () => store.removeListener(listenerId);
  }, []);

  useEffect(() => {
    if (!beaconCoordinator) return;
    const store = beaconCoordinator.getBeaconStore();
    if (document.activeElement !== streamTitleInputRef.current) {
      setStreamTitle(store.currentState.streamTitle ?? '');
    }
    const listenerId = store.addListener((s) => {
      if (document.activeElement !== streamTitleInputRef.current) {
        setStreamTitle(s.streamTitle ?? '');
      }
    });
    return () => store.removeListener(listenerId);
  }, []);

  const onSaveStreamTitle = () => {
    beaconCoordinator?.setStreamTitle(streamTitle);
    setShowSave(false);
  };

  const streamLifecycle = useStreamLifecycle({
    sessionCoordinator: sessionCoordinator,
    appCoordinator: appCoordinator,
    electronService: serviceContainer.electronService,
    streamUrl,
    streamTitle,
    onSaveStreamTitle,
  });

  const handleStreamTitleChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setStreamTitle(e.target.value);
    beaconCoordinator?.setStreamTitle(e.target.value);
    setShowSave(true);
  };

  const onStreamTitleBlur = () => {
    onSaveStreamTitle();
  };

  return (
    <div className='flex flex-1 flex-col items-center overflow-y-auto p-4'>
      <div className={clsx(isElectron ? 'max-w-[580px]' : 'max-w-[1280px]', 'flex h-fit w-full flex-col gap-4')}>
        <section className='flex flex-col gap-4'>
          <div className='flex flex-col justify-between gap-4 md:flex-row md:items-start'>
            <div className='order-last w-full md:order-first md:min-w-0 md:flex-1'>
              {displaySourceManager ? <BeaconDisplayPickerWithManager manager={displaySourceManager} /> : null}
            </div>
            <div className='relative w-full md:min-w-0 md:flex-1'>
              <Input
                ref={streamTitleInputRef}
                type='text'
                disabled={!streamLifecycle.canUpdateTitle}
                onChange={handleStreamTitleChanged}
                value={streamTitle}
                className='theme-midnight:shadow-[0px_0px_4px_1px_rgba(99,210,218,0.6)] theme-midnight:border-dark theme-gray:border-brighter theme-gray:placeholder:text-white/40 w-full border-2 bg-gray-800 p-2 pr-10 drop-shadow-lg placeholder:text-white focus:outline-none focus:ring-0'
                placeholder='Playcast Title...'
                data-testid='stream-title-input'
              />
              {showSave && (
                <button
                  onClick={onStreamTitleBlur}
                  className='absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-white'
                  data-testid='save-title-button'
                >
                  <FontAwesomeIcon icon={faFloppyDisk} className='text-emerald-400' />
                </button>
              )}
            </div>
          </div>
          <BeaconPreviewSection showPreview={showPreview} onTogglePreview={() => setShowPreview((prev) => !prev)} />
        </section>

        <section className='flex flex-1 flex-col gap-4 md:flex-row'>
          <div className='flex w-full md:w-1/2' tabIndex={-1}>
            {audioManager ? <BeaconVolumeSection manager={audioManager} /> : null}
          </div>
          <div className='relative mt-auto flex w-full flex-col gap-y-4 md:w-1/2'>
            <div className='flex gap-2'>
              <BeaconInputOptions />
              <BeaconShareMenu />
            </div>
            <div className='bottom-0 hidden flex-1 md:flex'>
              <LiveActionButton
                isLive={streamLifecycle.isLive}
                buttonText={streamLifecycle.buttonText}
                handleStopStream={streamLifecycle.stopStream}
                handleStartLoginStream={streamLifecycle.handleStartLoginStream}
              />
            </div>
          </div>
        </section>

        <div className='sticky bottom-0 flex flex-1 md:hidden'>
          <LiveActionButton
            isLive={streamLifecycle.isLive}
            buttonText={streamLifecycle.buttonText}
            handleStopStream={streamLifecycle.stopStream}
            handleStartLoginStream={streamLifecycle.handleStartLoginStream}
          />
        </div>
      </div>
    </div>
  );
}
